import { cn } from '@endure/ui';

import { Icon } from 'components/Icon';
import config, { type Config } from 'config';
import { useTranslation } from 'libs/translations';
import { useEffect, useRef, useState } from 'react';

export interface TestEnvWarningProps {
  className?: string;
  onHeightChange?: (height: number) => void;
}

export const shouldDisplayTestEnvWarning = ({
  environment,
  network,
}: Pick<Config, 'environment' | 'network'>) =>
  environment !== 'production' && (environment !== 'local' || network === 'bittensor_testnet');

export const isTestEnvWarningVisible = shouldDisplayTestEnvWarning(config);

export const TestEnvWarning: React.FC<TestEnvWarningProps> = ({ className, onHeightChange }) => {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);
  const warningRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const warningElement = warningRef.current;
    if (!warningElement || !onHeightChange) {
      return;
    }

    const reportHeight = () => onHeightChange(warningElement.getBoundingClientRect().height);
    reportHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', reportHeight);
      return () => window.removeEventListener('resize', reportHeight);
    }

    const resizeObserver = new ResizeObserver(reportHeight);
    resizeObserver.observe(warningElement);

    return () => resizeObserver.disconnect();
  }, [onHeightChange]);

  if (!isTestEnvWarningVisible) {
    return undefined;
  }

  return (
    <div
      ref={warningRef}
      aria-hidden={isDismissed || undefined}
      className={cn(
        'forge-test-warning relative px-12 py-2 font-semibold text-center',
        isDismissed && 'invisible',
        className,
      )}
    >
      {t('layout.testEnvWarning')}

      <button
        type="button"
        aria-label={t('modal.closeAriaLabel')}
        tabIndex={isDismissed ? -1 : undefined}
        onClick={() => setIsDismissed(true)}
        className="forge-test-warning__close absolute right-1 top-1/2 size-8 -translate-y-1/2 cursor-pointer rounded-full transition-colors"
      >
        <Icon name="close" className="m-auto size-4" />
      </button>
    </div>
  );
};
